import { enhancedApi } from '@store/generated/payments';

/**
 * Feature-specific tag types for cache management
 */
const PAYMENTS_TAG_TYPES = [
  'StripeConnectStatus',
  'StripeAccount',
  'StripeInvoices',
  'PatientPaymentMethods',
  'PatientInvoices',
  'BillingEncounter',
  'BillingEncountersList',
  // Cross-API tags owned by billing-v2 / eligibility-checks; registered here
  // so payment-side mutations can invalidate the patient billing queue,
  // financial summary, eligibility badge, and worklist row downstream of
  // a sent invoice / reminder.
  'BillingV2Overview',
  'BillingV2PatientBilling',
  'BillingV2PatientDetail',
  'PatientBalance',
  'Eligibility',
  'Worklist',
] as const;

/**
 * Payments API - extends the generated OpenAPI client
 * Provides Stripe Connect integration endpoints
 */
export const paymentsApi = enhancedApi.enhanceEndpoints({
  addTagTypes: PAYMENTS_TAG_TYPES,
  endpoints: {
    // Enhance generated endpoints with cache tags
    postV2ProviderStripeConnect: {
      invalidatesTags: [
        { type: 'StripeConnectStatus' as const },
        { type: 'StripeAccount' as const },
      ],
    },

    getV2ProviderStripeConnectStatus: {
      providesTags: [{ type: 'StripeConnectStatus' as const }],
    },

    postV2ProviderStripeDashboardLink: {
      // No cache invalidation needed for dashboard link generation
    },

    getV2ProviderStripeInvoices: {
      providesTags: result => [
        { type: 'StripeInvoices' as const },
        ...(result?.invoices || []).map(invoice => ({
          type: 'StripeInvoices' as const,
          id: invoice.id,
        })),
      ],
    },

    postV2ProviderPaymentRequests: {
      // Sending an invoice creates a PatientInvoice row, optionally creates
      // a charge_line (when charge_line_ids is omitted but the BE infers a
      // charge), and shifts the patient's billing status. Invalidate the
      // surfaces that read off any of those: patient invoice list, financial
      // summary (Patient Ledger Drawer), patient billing queue, and the
      // eligibility badge / worklist row (which both watch
      // patient_due_status flips downstream of the invoice).
      invalidatesTags: (_result, _error, { sendPaymentRequestRequest }) => [
        { type: 'PatientBalance' as const, id: sendPaymentRequestRequest.patient_id },
        { type: 'PatientInvoices' as const, id: sendPaymentRequestRequest.patient_id },
        { type: 'BillingV2PatientDetail' as const, id: sendPaymentRequestRequest.patient_id },
        { type: 'BillingV2PatientBilling' as const },
        { type: 'BillingV2Overview' as const },
        { type: 'BillingEncountersList' as const, id: 'UNIFIED_WORKLIST' },
        { type: 'BillingEncounter' as const },
        ...(sendPaymentRequestRequest.encounter_id
          ? [
              {
                type: 'BillingEncounter' as const,
                id: sendPaymentRequestRequest.encounter_id,
              },
            ]
          : []),
        { type: 'Eligibility' as const },
        { type: 'Worklist' as const },
      ],
    },

    postV2ProviderInvoicesByInvoiceIdRemind: {
      // A reminder send extends the 7-day suppression window and bumps
      // reminder_count / latest_action_at on the queue row, so invalidate
      // the patient billing queue and the patient's financial summary.
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: 'PatientInvoices' as const, id: invoiceId },
        { type: 'BillingV2PatientBilling' as const },
        { type: 'BillingV2PatientDetail' as const },
      ],
    },

    postV2ProviderInvoicesByInvoiceIdReconcile: {
      // Reconciliation voids an active invoice, may create a replacement,
      // and changes charge_line patient_due_status values. Refresh every
      // billing surface that summarizes invoice coverage or patient A/R.
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: 'PatientInvoices' as const, id: invoiceId },
        { type: 'BillingV2PatientBilling' as const },
        { type: 'BillingV2PatientDetail' as const },
        { type: 'BillingV2Overview' as const },
        { type: 'Eligibility' as const },
        { type: 'Worklist' as const },
      ],
    },

    // Patient Payment Methods endpoints
    getV2ProviderPatientsByPatientIdPaymentMethods: {
      providesTags: (result, _error, { patientId }) =>
        result?.payment_methods
          ? [
              ...result.payment_methods.map(pm => ({
                type: 'PatientPaymentMethods' as const,
                id: pm.id,
              })),
              { type: 'PatientPaymentMethods' as const, id: patientId },
            ]
          : [{ type: 'PatientPaymentMethods' as const, id: patientId }],
    },

    getV2ProviderPatientsByPatientIdPaymentMethodsStatus: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientPaymentMethods' as const, id: `${patientId}-status` },
      ],
    },

    postV2ProviderPatientsByPatientIdPaymentMethodsSetup: {
      // Setup intent creation doesn't need cache invalidation
    },

    postV2ProviderPatientsByPatientIdPaymentMethodsConfirm: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientPaymentMethods' as const, id: patientId },
        { type: 'PatientPaymentMethods' as const, id: `${patientId}-status` },
      ],
    },

    deleteV2ProviderPatientsByPatientIdPaymentMethodsAndPaymentMethodId: {
      invalidatesTags: (_result, _error, { patientId, paymentMethodId }) => [
        { type: 'PatientPaymentMethods' as const, id: paymentMethodId },
        { type: 'PatientPaymentMethods' as const, id: patientId },
        { type: 'PatientPaymentMethods' as const, id: `${patientId}-status` },
      ],
    },

    postV2ProviderPatientsByPatientIdPaymentMethodsAndPaymentMethodIdDefault: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientPaymentMethods' as const, id: patientId },
      ],
    },

    // Invoice URLs - fetch fresh on each click, never cache
    getV2ProviderPatientsByPatientIdInvoicesAndInvoiceIdUrls: {
      // No cache tags — URLs expire quickly, always fetch fresh
      keepUnusedDataFor: 0,
    },

    // Patient Invoices endpoint
    getV2ProviderPatientsByPatientIdInvoices: {
      providesTags: (result, _error, { patientId }) =>
        result?.invoices
          ? [
              ...result.invoices.map(invoice => ({
                type: 'PatientInvoices' as const,
                id: invoice.id,
              })),
              { type: 'PatientInvoices' as const, id: patientId },
            ]
          : [{ type: 'PatientInvoices' as const, id: patientId }],
    },
  },
});

// Export hooks with user-friendly names
export const {
  // Stripe Connect hooks
  usePostV2ProviderStripeConnectMutation: useCreateStripeConnectMutation,
  useGetV2ProviderStripeConnectStatusQuery: useGetStripeConnectStatusQuery,
  usePostV2ProviderStripeDashboardLinkMutation: useCreateStripeDashboardLinkMutation,
  useGetV2ProviderStripeInvoicesQuery: useGetStripeInvoicesQuery,
  useLazyGetV2ProviderStripeInvoicesQuery: useLazyGetStripeInvoicesQuery,
  usePostV2ProviderPaymentRequestsMutation: useSendPaymentRequestMutation,
  usePostV2ProviderInvoicesByInvoiceIdRemindMutation: useSendInvoiceReminderMutation,
  usePostV2ProviderInvoicesByInvoiceIdReconcileMutation: useReconcileInvoiceMutation,

  // Patient Payment Methods hooks
  useGetV2ProviderPatientsByPatientIdPaymentMethodsQuery: useGetPatientPaymentMethodsQuery,
  useGetV2ProviderPatientsByPatientIdPaymentMethodsStatusQuery: useGetPatientPaymentMethodStatusQuery,
  usePostV2ProviderPatientsByPatientIdPaymentMethodsSetupMutation: useSetupPatientPaymentMethodMutation,
  usePostV2ProviderPatientsByPatientIdPaymentMethodsConfirmMutation: useConfirmPatientPaymentMethodMutation,
  useDeleteV2ProviderPatientsByPatientIdPaymentMethodsAndPaymentMethodIdMutation: useDeletePatientPaymentMethodMutation,
  usePostV2ProviderPatientsByPatientIdPaymentMethodsAndPaymentMethodIdDefaultMutation: useSetDefaultPatientPaymentMethodMutation,

  // Patient Invoices hooks
  useGetV2ProviderPatientsByPatientIdInvoicesQuery: useGetPatientInvoicesQuery,

  // Invoice URLs - lazy query for on-click fresh URL fetching
  useLazyGetV2ProviderPatientsByPatientIdInvoicesAndInvoiceIdUrlsQuery: useLazyGetInvoiceUrlsQuery,
} = paymentsApi;
